<template>
	<validation-provider
		#default="{ errors }"
		ref="customValidator"
		:name="fieldName || $props.label"
		:rules="rules"
		:mode="validationMode"
		:custom-messages="customMessages"
		:detect-input="validateField"
		:skip-if-empty="false"
		tag="div"
		:class="tooltipText || reserveTooltipSpace ? 'd-flex flex-row cassie-horizontal-sm' : ''"
	>
		<div class="dropdown-wrapper">
			<v-select
				v-if="!searchable"
				ref="select"
				:label="label"
				v-bind="$props"
				:readonly="viewOnly"
				:persistent-placeholder="viewOnly"
				:error-messages="errors.length ? [errors[0]] : []"
				:rules="[]"
				:items="sortedItems"
				:hide-details="!hasRules || !errors.length"
				:class="[small ? 'cassie-dropdown--small' : '', getQaSelectorClass($props)]"
				:menu-props="{
					contentClass: `dropdown-menu cassie-dropdown ${small ? 'cassie-dropdown--small' : '' }`,
					value: menuOpen
				}"
				:data-testid="testId"
				dense
				class="cassie-dropdown"
				v-on="$listeners"
				@click="menuOpen = true"
			>
				<template
					v-if="htmlErrorMessage"
					#message="{ message }"
				>
					<span
						v-dompurify-html="message"
					/>
				</template>
				<template
					v-if="selectAll"
					#prepend-item
				>
					<slot
						name="prepend-item"
					/>
				</template>
				<template
					v-if="multiple"
					#selection="{ item, index }"
				>
					<span class="v-select__selection">
						<span
							v-if="index === 0"
							class="mr-1"
						>
							{{ itemText ? value[0] : item }}
						</span>
						<span
							v-if="value && index === 1"
							class="grey--text text-caption"
						>
							(+{{ value.length - 1 }} others)
						</span>
					</span>
				</template>
				<template
					v-else-if="$scopedSlots.selection"
					#selection="props"
				>
					<slot
						name="selection"
						v-bind="props"
					/>
				</template>
				<template
					v-else-if="showItemChips"
					#selection="{ item }"
				>
					<div
						class="d-flex align-center"
						style="width: 100%;"
					>
						<span>
							{{ item.text }}
						</span>
						<Chip
							v-if="item.chipLabel"
							:color="item.chipColor"
							class="ml-2 chip-size"
						>
							{{ item.chipLabel }}
						</Chip>
					</div>
				</template>
				<template
					v-if="showItemChips"
					#item="{ item }"
				>
					<div
						class="d-flex align-center"
						style="width: 100%;"
					>
						<span>
							{{ item.text }}
						</span>
						<Chip
							v-if="item.chipLabel"
							:color="item.chipColor"
							class="ml-2"
						>
							{{ item.chipLabel }}
						</Chip>
					</div>
				</template>
			</v-select>
			<v-autocomplete
				v-else
				ref="select"
				:label="label"
				v-bind="$props"
				:readonly="viewOnly"
				:persistent-placeholder="viewOnly"
				:error-messages="errors.length ? [errors[0]] : []"
				:rules="[]"
				:items="sortedItems"
				:hide-details="!hasRules || !errors.length"
				:class="[small ? 'cassie-dropdown--small' : '', getQaSelectorClass($props), multiple ? 'cassie-multi-dropdown' : '']"
				:menu-props="{
					contentClass: `dropdown-menu cassie-dropdown ${small ? 'cassie-dropdown--small' : '' }`,
				}"
				data-searchable="true"
				:data-testid="testId"
				dense
				:chips="multiple"
				:deletable-chips="multiple"
				:small-chips="multiple"
				prepend-inner-icon="mdi-magnify"
				class="cassie-dropdown"
				v-on="$listeners"
			>
				<template
					v-if="htmlErrorMessage"
					#message="{ message }"
				>
					<span
						v-dompurify-html="message"
					/>
				</template>
				<template
					v-if="selectAll"
					#prepend-item
				>
					<slot
						name="prepend-item"
					/>
				</template>
				<template
					v-if="showItemChips && !multiple"
					#selection="{ item }"
				>
					<div
						class="d-flex align-center"
						style="width: 100%;"
					>
						<span>
							{{ item.text }}
						</span>
						<Chip
							v-if="item.chipLabel"
							:color="item.chipColor"
							class="ml-2 chip-size"
						>
							{{ item.chipLabel }}
						</Chip>
					</div>
				</template>
				<template
					v-if="showItemChips"
					#item="{ item }"
				>
					<div
						class="d-flex align-center"
						style="width: 100%;"
					>
						<span>
							{{ item.text }}
						</span>
						<Chip
							v-if="item.chipLabel"
							:color="item.chipColor"
							class="ml-2"
						>
							{{ item.chipLabel }}
						</Chip>
					</div>
				</template>
			</v-autocomplete>
			<div
				v-if="explainer"
				class="dropdown-explainer"
			>
				{{ explainer }}
			</div>
		</div>
		<v-tooltip
			v-if="tooltipText"
			right
		>
			<template #activator="{ on }">
				<v-icon
					light
					v-on="on"
				>
					mdi-information
				</v-icon>
			</template>
			<div v-dompurify-html="tooltipText" />
		</v-tooltip>
		<div
			v-if="reserveTooltipSpace"
			style="width: 25px;"
		/>
	</validation-provider>
</template>

<script>
import { VSelect } from 'vuetify/lib'
import FieldValidationMixin from '../validation/field-validation-mixin.js'
import Chip from './chip.vue'

export default {
	name: 'dropdown',
	components: {
		Chip
	},
	extends: VSelect,
	mixins: [FieldValidationMixin],
	props: {
		label: {
			type: String
		},
		viewOnly: {
			type: Boolean,
			default: false
		},
		light: {
			type: Boolean,
			default: undefined // Let dropdown inherit theme from app (was: true)
		},
		outlined: {
			type: Boolean,
			default: true
		},
		small: {
			type: Boolean,
			default: false
		},
		validateOnShow: {
			type: Boolean,
			default: false
		},
		tooltipText: {
			type: String
		},
		selectAll: {
			type: Boolean,
			default: false
		},
		customSort: {
			type: Boolean,
			default: false
		},
		reserveTooltipSpace: {
			type: Boolean,
			default: false
		},
		testId: {
			type: String,
			default: 'cassie_dropdown'
		},
		searchable: {
			type: Boolean,
			default: false
		},
		explainer: {
			type: String
		},
		htmlErrorMessage: {
			type: Boolean,
			default: false
		},
		showItemChips: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			menuOpen: false
		}
	},
	computed: {
		sortedItems () {
			const items = this.$props.items
			if (!items) return items
			if (this.customSort) return items
			const copy = Array.isArray(items) ? items.slice() : items
			return Array.isArray(copy)
				? copy.sort((a, b) => (a?.text || '').localeCompare(b?.text || ''))
				: copy
		}
	},
	mounted () {
		if (this.validateOnShow) {
			// Ensure the validation provider ref exists before calling validate
			this.$nextTick(() => {
				const provider = this.$refs.customValidator
				if (provider && typeof provider.validate === 'function') {
					provider.validate()
				}
			})
		}
	},
	created () {
		window.addEventListener('scroll', this.closeMenuOnScroll, true)
		window.addEventListener('click', this.closeMenu, true)
	},
	destroyed () {
		window.removeEventListener('scroll', this.closeMenuOnScroll, true)
		window.removeEventListener('click', this.closeMenu, true)
	},
	methods: {
		async validate () {
			const provider = this.$refs.customValidator
			if (!provider || typeof provider.validate !== 'function') {
				// If the provider is not yet available, treat as valid to avoid runtime errors
				return true
			}
			return await provider.validate()
		},
		getQaSelectorClass (props) {
			let trimmedLabel = props.label
			if (trimmedLabel) {
				if (trimmedLabel.includes(' *')) {
					trimmedLabel = trimmedLabel.replace(' *', '')
				}
				trimmedLabel = trimmedLabel.replace(/\s/g, '-').toLowerCase()
			}
			return `qa-dropdown-${trimmedLabel}`
		},
		closeMenuOnScroll (event) {
			if (!this.searchable && this.menuOpen && !event.target.closest('.dropdown-menu')) this.closeMenu(event)
			else if (this.searchable && !event.target.closest('.dropdown-menu')) {
				const dropdownMenu = document.querySelector('.dropdown-menu')
				if (dropdownMenu) {
					dropdownMenu.style.display = 'none'
				}
				this.$refs.select.blur()
			}
		},
		async closeMenu (event) {
			const searchableInputField = event.target.dataset.searchable === 'true'
			if ((!event.target.closest('.dropdown-menu') || !this.multiple) && this.menuOpen && !searchableInputField) {
				this.menuOpen = false
				this.$refs.select.blur()
			}
		}
	}
}
</script>

<style lang="scss">
	.dropdown-wrapper {
		flex: 1;
	}
	.dropdown-explainer {
	font-size: 0.75rem;
	font-family: 'Inter', sans-serif;
	color: var(--global-muted-foreground);
	margin-top: 4px;
	line-height: 1.4;
}
	.cassie-dropdown {
		.v-input {
			font-size: 0.875rem !important;
		}
		.v-select-list .v-list-item .v-list-item__action {
			margin: 5px 10px 0 0 !important;
		}

		// Label and input font styling - aligned with NextJS (text-sm = 0.875rem, font-medium = 500)
		// Note: Global styles in app.vue also apply these, but component-level provides specificity
		.v-input__control .v-input__slot .v-select__slot .v-label {
			font-size: 0.875rem !important;
			font-family: 'Inter', sans-serif;
			font-weight: 500;
		}

		.v-input__control .v-input__slot .v-select__slot input {
			font-size: 0.875rem !important;
			font-family: 'Inter', sans-serif;
			font-weight: 400;
		}

		// Selected value text
		.v-input__control .v-input__slot .v-select__slot .v-select__selection {
			font-size: 0.875rem !important;
			font-family: 'Inter', sans-serif;
			font-weight: 400;
		}

		// Placeholder text
		.v-input__control .v-input__slot .v-select__slot input::placeholder {
			font-size: 0.875rem !important;
			font-family: 'Inter', sans-serif;
		}

		// Dropdown item font size - aligned with NextJS (text-sm = 0.875rem)
		.v-select-list .v-list-item .v-list-item__content .v-list-item__title {
			font-size: 0.875rem !important;
			font-family: 'Inter', sans-serif;
		}

		// Change focus/active outline color from primary to syrenis-black
		&.v-input--is-focused .v-input__slot {
			border-color: var(--global-syrenis-black) !important;
		}

		&.v-input--is-focused:not(.v-input--has-state) .v-label {
			color: var(--global-syrenis-black) !important;
		}

		// Change icon colors to syrenis-black when focused
		&.v-input--is-focused {
			.v-input__append-inner .v-icon,
			.v-input__prepend-inner .v-icon,
			.v-select__selection .v-icon {
				color: var(--global-syrenis-black) !important;
			}
		}

		.v-input__slot fieldset {
			border-color: inherit !important;
		}

		&--small {
			.v-input__control .v-input__slot {
				height: 32px !important;
				padding: 0 8px !important;
				min-height: 0 !important;
			}

			.v-select-list .v-list-item {
				min-height: 0 !important;
			}

			.v-select-list .v-list-item .v-list-item__content {
				padding: 4px 0 !important;
			}

			.v-select__selection.v-select__selection {
				font-size: 85% !important;
				margin-left: 6px;
			}

			.v-input__append-inner {
				margin-top: 4px !important;
			}

			.v-input__control .v-input__slot label {
				margin-top: -12px !important;
			}

			.v-label {
				top: 18px !important;
				font-size: 14px !important;
			}

			.v-input__control .v-input__slot label.v-label--active {
				margin: 0 0 0 5px !important;
			}

			.v-label--active {
				-webkit-transform: translateY(-25px) scale(0.65) !important;
				transform: translateY(-25px) scale(0.65) !important;
			}

		}
		.v-select__selections .v-chip {
			height: 24px !important;
			font-size: 12px !important;
		}
		// Input background - white in light mode, dark background in dark mode
		.v-input__slot {
			background-color: var(--global-card-background) !important;
		}

		// Placeholder text styling
		input::placeholder {
			color: var(--global-muted-foreground);
			opacity: 0.7;
		}
	}

	.cassie-multi-dropdown {
		.v-chip {
			margin: 8px !important;
		}
	}

	.chip-size {
		max-height: 16px;
		font-size: 10px !important;
	}

	// Dropdown menu styling for dark mode
	.dropdown-menu {
		// Menu background
		background-color: var(--global-card-background) !important;

		.v-list {
			background-color: var(--global-card-background) !important;
		}

		// Selected item styling - use primary color with appropriate opacity
		.v-list-item--active {
			background-color: var(--v-primary-base, var(--global-primary)) !important;
			color: white !important;

			.v-list-item__title {
				color: white !important;
			}

			// Checkmark icon for selected items
			.v-list-item__action .v-icon {
				color: white !important;
			}

			// Target custom template content (e.g., showItemChips)
			span {
				color: white !important;
			}
		}

		// Hover/highlighted styling - subtle highlight
		.v-list-item--highlighted:not(.v-list-item--active),
		.v-list-item:hover:not(.v-list-item--active) {
			background-color: var(--global-muted, rgba(0, 0, 0, 0.04)) !important;
		}

		// Ensure text is readable
		.v-list-item__title {
			color: var(--global-foreground) !important;
		}
	}
</style>
